import { useAutoAnimate } from '@formkit/auto-animate/react';
import { PlusIcon } from '@heroicons/react/20/solid';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger
} from 'components/layout/popover';
import Tooltip from 'components/layout/tooltip';
import {
  AboveGradeWallType,
  ApplianceType,
  BasementFloorType,
  BelowGradeWallType,
  CeilingBelowAtticType,
  CeilingBelowRoofType,
  ExposureDirectionForCompassType,
  FloorOverExposedSpaceType,
  FloorOverUnconditionedSpaceType,
  SizingCalculatorAttributesType,
  SizingItemsType,
  SlabFloorType
} from 'data/types';
import { setDefaultSizingItems } from 'features/data/data-slice';
import { nanoid } from 'nanoid';
import { useEffect, useRef, useState } from 'react';
import { SizingItemsOrderType } from 'utils/sizing';
import ApplianceComponent from './appliance';
import CeilingBelowAtticComponent from './ceilings/ceiling-below-attic';
import CeilingBelowRoofComponent from './ceilings/ceiling-below-roof';
import CompassPopover from './compass-popover';
import BasementFloorComponent from './floors/basement-floor';
import FloorOverExposedSpaceComponent from './floors/floor-over-exposed-space';
import FloorOverUnconditionedSpaceComponent from './floors/floor-over-unconditioned-space';
import SlabFloorComponent from './floors/slab-floor';
import AboveGradeWallComponent from './walls/above-grade-wall';
import BelowGradeWallComponent from './walls/below-grade-wall';

type SizingItemsProps = {
  sizingAttributes?: SizingCalculatorAttributesType;
  updateSizingAttributes: (
    updates: Partial<SizingCalculatorAttributesType>
  ) => void;
  defaultArea: number;
  defaultWallHeight: number;
  defaultWallWidth: number;
  orderType?: SizingItemsOrderType;
  excludeWalls?: boolean;
  excludeCeilings?: boolean;
  excludeFloors?: boolean;
};

export default function SizingItemsComponent({
  sizingAttributes,
  updateSizingAttributes,
  defaultArea,
  defaultWallHeight,
  defaultWallWidth,
  orderType = 'default',
  excludeWalls = false,
  excludeCeilings = false,
  excludeFloors = false
}: SizingItemsProps) {
  const { defaultSizingItems, applianceItems } = useAppSelector(
    (state) => state.data
  );

  const aboveGradeWalls = sizingAttributes?.aboveGradeWalls || [];
  const belowGradeWalls = sizingAttributes?.belowGradeWalls || [];
  const ceilingsBelowRoof = sizingAttributes?.ceilingsBelowRoof || [];
  const ceilingsBelowAttic = sizingAttributes?.ceilingsBelowAttic || [];
  const floorsOverUnconditionedSpace =
    sizingAttributes?.floorsOverUnconditionedSpace || [];
  const floorsOverExposedSpace = sizingAttributes?.floorsOverExposedSpace || [];
  const slabFloors = sizingAttributes?.slabFloors || [];
  const basementFloors = sizingAttributes?.basementFloors || [];
  const appliances = sizingAttributes?.appliances || [];

  const [defaultFloorExposedEdge, setDefaultFloorExposedEdge] = useState(0);
  const [animate] = useAutoAnimate({ duration: 100 });
  const dispatch = useAppDispatch();
  const ceilingsAndFloorsDisabled = !defaultArea;
  const wallsDisabled = !defaultWallHeight || !defaultWallWidth;
  const hasRunAutoSyncRef = useRef(false);

  useEffect(() => {
    if (!sizingAttributes) return;
    const newDefaultFloorExposedEdge = sizingAttributes.aboveGradeWalls.reduce(
      (acc, wall) => acc + wall.width,
      0
    );

    setDefaultFloorExposedEdge(newDefaultFloorExposedEdge);

    if (!hasRunAutoSyncRef.current) {
      hasRunAutoSyncRef.current = true;
      return;
    }

    let anyChanged = false;
    const updatedSlabFloors = sizingAttributes.slabFloors.map((floor) => {
      if (floor.modified) return floor;
      if (floor.exposedEdgeFeet === newDefaultFloorExposedEdge) return floor;
      anyChanged = true;
      return { ...floor, exposedEdgeFeet: newDefaultFloorExposedEdge };
    });

    if (anyChanged) {
      updateSizingAttributes({ slabFloors: updatedSlabFloors });
    }
  }, [JSON.stringify(aboveGradeWalls)]);

  const applyToDefaultSizingItems = (updates: Partial<SizingItemsType>) => {
    dispatch(
      setDefaultSizingItems({
        ...defaultSizingItems,
        ...updates
      })
    );
  };

  const addCeilingBelowRoof = () => {
    updateSizingAttributes({
      ceilingsBelowRoof: [
        ...ceilingsBelowRoof,
        {
          ...defaultSizingItems.ceilingBelowRoofJoists,
          uniqueId: nanoid(5),
          valid: true,
          area: defaultArea,
          skylights: []
        } as CeilingBelowRoofType
      ]
    });
  };

  const addCeilingOnExposedBeams = () => {
    updateSizingAttributes({
      ceilingsBelowRoof: [
        ...ceilingsBelowRoof,
        {
          ...defaultSizingItems.ceilingOnExposedBeams,
          uniqueId: nanoid(5),
          valid: true,
          area: defaultArea,
          skylights: []
        } as CeilingBelowRoofType
      ]
    });
  };

  const updateCeilingBelowRoof = (
    ceilingOrFenestrationId: string,
    updates: Partial<CeilingBelowRoofType>
  ) => {
    updateSizingAttributes({
      ceilingsBelowRoof: ceilingsBelowRoof.map((ceiling) =>
        ceiling.uniqueId === ceilingOrFenestrationId
          ? {
              ...ceiling,
              ...updates,
              modified: true
            }
          : {
              ...ceiling,
              skylights: (ceiling.skylights || []).map((skylight) =>
                skylight.uniqueId === ceilingOrFenestrationId
                  ? { ...skylight, ...updates, modified: true }
                  : skylight
              )
            }
      )
    });
  };

  const duplicateCeilingBelowRoof = (uniqueId: string) => {
    const ceiling = ceilingsBelowRoof.find((c) => c.uniqueId === uniqueId);

    if (ceiling) {
      updateSizingAttributes({
        ceilingsBelowRoof: [
          ...ceilingsBelowRoof,
          { ...ceiling, uniqueId: nanoid(5) }
        ]
      });
    }
  };

  const addCeilingBelowAttic = () => {
    updateSizingAttributes({
      ceilingsBelowAttic: [
        ...ceilingsBelowAttic,
        {
          ...defaultSizingItems.ceilingBelowAttic,
          uniqueId: nanoid(5),
          valid: true,
          area: defaultArea
        } as CeilingBelowAtticType
      ]
    });
  };

  const updateCeilingBelowAttic = (
    ceilingId: string,
    updates: Partial<CeilingBelowAtticType>
  ) => {
    updateSizingAttributes({
      ceilingsBelowAttic: ceilingsBelowAttic.map((ceiling) =>
        ceiling.uniqueId === ceilingId
          ? { ...ceiling, ...updates, modified: true }
          : ceiling
      )
    });
  };

  const duplicateCeilingBelowAttic = (uniqueId: string) => {
    const ceiling = ceilingsBelowAttic.find((c) => c.uniqueId === uniqueId);

    if (ceiling) {
      updateSizingAttributes({
        ceilingsBelowAttic: [
          ...ceilingsBelowAttic,
          { ...ceiling, uniqueId: nanoid(5) }
        ]
      });
    }
  };

  const deleteCeiling = (ceilingId: string) => {
    updateSizingAttributes({
      ceilingsBelowRoof: ceilingsBelowRoof.filter(
        (ceiling) => ceiling.uniqueId !== ceilingId
      ),
      ceilingsBelowAttic: ceilingsBelowAttic.filter(
        (ceiling) => ceiling.uniqueId !== ceilingId
      )
    });
  };

  const addAboveGradeWall = (direction: ExposureDirectionForCompassType) => {
    updateSizingAttributes({
      aboveGradeWalls: [
        ...aboveGradeWalls,
        {
          ...defaultSizingItems.aboveGradeWall,
          uniqueId: nanoid(5),
          valid: true,
          exposure: direction,
          width: defaultWallWidth,
          height: defaultWallHeight,
          verticalGlass: [],
          solidDoors: []
        } as AboveGradeWallType
      ]
    });
  };

  const updateAboveGradeWall = (
    wallOrFenestrationId: string,
    updates: Partial<AboveGradeWallType>
  ) => {
    updateSizingAttributes({
      aboveGradeWalls: aboveGradeWalls.map((wall) =>
        wall.uniqueId === wallOrFenestrationId
          ? {
              ...wall,
              ...updates,
              modified: true
            }
          : {
              ...wall,
              verticalGlass: (wall.verticalGlass || []).map((glass) =>
                glass.uniqueId === wallOrFenestrationId
                  ? { ...glass, ...updates, modified: true }
                  : glass
              ),
              solidDoors: (wall.solidDoors || []).map((door) =>
                door.uniqueId === wallOrFenestrationId
                  ? { ...door, ...updates, modified: true }
                  : door
              )
            }
      )
    });
  };

  const duplicateAboveGradeWall = (uniqueId: string) => {
    const wall = aboveGradeWalls.find((w) => w.uniqueId === uniqueId);

    if (wall) {
      updateSizingAttributes({
        aboveGradeWalls: [...aboveGradeWalls, { ...wall, uniqueId: nanoid(5) }]
      });
    }
  };

  const addBelowGradeWall = (direction: ExposureDirectionForCompassType) => {
    updateSizingAttributes({
      belowGradeWalls: [
        ...belowGradeWalls,
        {
          ...defaultSizingItems.belowGradeWall,
          uniqueId: nanoid(5),
          valid: true,
          exposure: direction,
          width: defaultWallWidth,
          height: defaultWallHeight,
          verticalGlass: [],
          solidDoors: []
        } as BelowGradeWallType
      ]
    });
  };

  const updateBelowGradeWall = (
    wallOrFenestrationId: string,
    updates: Partial<BelowGradeWallType>
  ) => {
    updateSizingAttributes({
      belowGradeWalls: belowGradeWalls.map((wall) =>
        wall.uniqueId === wallOrFenestrationId
          ? {
              ...wall,
              ...updates,
              modified: true
            }
          : {
              ...wall,
              verticalGlass: (wall.verticalGlass || []).map((glass) =>
                glass.uniqueId === wallOrFenestrationId
                  ? { ...glass, ...updates, modified: true }
                  : glass
              ),
              solidDoors: (wall.solidDoors || []).map((door) =>
                door.uniqueId === wallOrFenestrationId
                  ? { ...door, ...updates, modified: true }
                  : door
              )
            }
      )
    });
  };

  const duplicateBelowGradeWall = (uniqueId: string) => {
    const wall = belowGradeWalls.find((w) => w.uniqueId === uniqueId);

    if (wall) {
      updateSizingAttributes({
        belowGradeWalls: [...belowGradeWalls, { ...wall, uniqueId: nanoid(5) }]
      });
    }
  };

  const deleteWall = (wallId: string) => {
    updateSizingAttributes({
      aboveGradeWalls: aboveGradeWalls.filter(
        (wall) => wall.uniqueId !== wallId
      ),
      belowGradeWalls: belowGradeWalls.filter(
        (wall) => wall.uniqueId !== wallId
      )
    });
  };

  const addFloorOverUnconditionedSpace = () => {
    updateSizingAttributes({
      floorsOverUnconditionedSpace: [
        ...floorsOverUnconditionedSpace,
        {
          ...defaultSizingItems.floorOverUnconditionedSpace,
          uniqueId: nanoid(5),
          valid: true,
          area: defaultArea
        } as FloorOverUnconditionedSpaceType
      ]
    });
  };

  const updateFloorOverUnconditionedSpace = (
    floorId: string,
    updates: Partial<FloorOverUnconditionedSpaceType>
  ) => {
    updateSizingAttributes({
      floorsOverUnconditionedSpace: floorsOverUnconditionedSpace.map((floor) =>
        floor.uniqueId === floorId
          ? { ...floor, ...updates, modified: true }
          : floor
      )
    });
  };

  const duplicateFloorOverUnconditionedSpace = (uniqueId: string) => {
    const floor = floorsOverUnconditionedSpace.find(
      (f) => f.uniqueId === uniqueId
    );

    if (floor) {
      updateSizingAttributes({
        floorsOverUnconditionedSpace: [
          ...floorsOverUnconditionedSpace,
          { ...floor, uniqueId: nanoid(5) }
        ]
      });
    }
  };

  const addFloorOverExposedSpace = () => {
    updateSizingAttributes({
      floorsOverExposedSpace: [
        ...floorsOverExposedSpace,
        {
          ...defaultSizingItems.floorOverExposedSpace,
          uniqueId: nanoid(5),
          valid: true,
          area: defaultArea
        } as FloorOverExposedSpaceType
      ]
    });
  };

  const updateFloorOverExposedSpace = (
    floorId: string,
    updates: Partial<FloorOverExposedSpaceType>
  ) => {
    updateSizingAttributes({
      floorsOverExposedSpace: floorsOverExposedSpace.map((floor) =>
        floor.uniqueId === floorId
          ? { ...floor, ...updates, modified: true }
          : floor
      )
    });
  };

  const duplicateFloorOverExposedSpace = (uniqueId: string) => {
    const floor = floorsOverExposedSpace.find((f) => f.uniqueId === uniqueId);

    if (floor) {
      updateSizingAttributes({
        floorsOverExposedSpace: [
          ...floorsOverExposedSpace,
          { ...floor, uniqueId: nanoid(5) }
        ]
      });
    }
  };

  const addSlabFloor = () => {
    updateSizingAttributes({
      slabFloors: [
        ...slabFloors,
        {
          ...defaultSizingItems.slabFloor,
          uniqueId: nanoid(5),
          valid: true,
          exposedEdgeFeet: defaultFloorExposedEdge,
          feetBelowGrade: 1
        } as SlabFloorType
      ]
    });
  };

  const updateSlabFloor = (
    floorId: string,
    updates: Partial<SlabFloorType>
  ) => {
    updateSizingAttributes({
      slabFloors: slabFloors.map((floor) =>
        floor.uniqueId === floorId
          ? { ...floor, ...updates, modified: true }
          : floor
      )
    });
  };

  const duplicateSlabFloor = (uniqueId: string) => {
    const floor = slabFloors.find((f) => f.uniqueId === uniqueId);

    if (floor) {
      updateSizingAttributes({
        slabFloors: [...slabFloors, { ...floor, uniqueId: nanoid(5) }]
      });
    }
  };

  const addBasementFloor = () => {
    const shortestSideWidth =
      [20, 24, 28, 32].find((width) => width >= defaultWallWidth) || 32;

    updateSizingAttributes({
      basementFloors: [
        ...basementFloors,
        {
          ...defaultSizingItems.basementFloor,
          uniqueId: nanoid(5),
          valid: true,
          area: defaultArea,
          shortestSideWidthFeet: shortestSideWidth
        } as BasementFloorType
      ]
    });
  };

  const updateBasementFloor = (
    floorId: string,
    updates: Partial<BasementFloorType>
  ) => {
    updateSizingAttributes({
      basementFloors: basementFloors.map((floor) =>
        floor.uniqueId === floorId
          ? { ...floor, ...updates, modified: true }
          : floor
      )
    });
  };

  const duplicateBasementFloor = (uniqueId: string) => {
    const floor = basementFloors.find((f) => f.uniqueId === uniqueId);

    if (floor) {
      updateSizingAttributes({
        basementFloors: [...basementFloors, { ...floor, uniqueId: nanoid(5) }]
      });
    }
  };

  const deleteFloor = (floorId: string) => {
    updateSizingAttributes({
      floorsOverUnconditionedSpace: floorsOverUnconditionedSpace.filter(
        (floor) => floor.uniqueId !== floorId
      ),
      floorsOverExposedSpace: floorsOverExposedSpace.filter(
        (floor) => floor.uniqueId !== floorId
      ),
      slabFloors: slabFloors.filter((floor) => floor.uniqueId !== floorId),
      basementFloors: basementFloors.filter(
        (floor) => floor.uniqueId !== floorId
      )
    });
  };

  const addAppliance = (itemId?: number) => {
    const defaultValues =
      applianceItems.find((a) => a.itemId === itemId) ??
      defaultSizingItems.appliance;
    const newAppliance: ApplianceType =
      itemId !== undefined
        ? {
            uniqueId: nanoid(5),
            itemId,
            label: defaultValues?.label || 'Appliance',
            wattage: defaultValues?.wattage || 0,
            inUseOutput: defaultValues?.inUseOutput || 0,
            perHourUsage: defaultValues?.perHourUsage || 0,
            custom: false
          }
        : {
            uniqueId: nanoid(5),
            itemId,
            label: defaultValues?.label || 'Appliance',
            wattage: defaultValues?.wattage || 0,
            inUseOutput: defaultValues?.inUseOutput || 0,
            perHourUsage: defaultValues?.perHourUsage || 0,
            custom: true
          };

    updateSizingAttributes({
      appliances: [...appliances, newAppliance]
    });
  };

  const updateAppliance = (
    applianceId: string,
    updates: Partial<ApplianceType>
  ) => {
    updateSizingAttributes({
      appliances: appliances.map((appliance) =>
        appliance.uniqueId === applianceId
          ? { ...appliance, ...updates, modified: true }
          : appliance
      ) as ApplianceType[]
    });
  };

  const duplicateAppliance = (uniqueId: string) => {
    const appliance = appliances.find((a) => a.uniqueId === uniqueId);

    if (appliance) {
      updateSizingAttributes({
        appliances: [...appliances, { ...appliance, uniqueId: nanoid(5) }]
      });
    }
  };

  const deleteAppliance = (applianceId: string) => {
    updateSizingAttributes({
      appliances: appliances.filter(
        (appliance) => appliance.uniqueId !== applianceId
      )
    });
  };

  const buttonWrapperClasses = (disabled: boolean) =>
    clsx('relative', disabled && 'cursor-not-allowed opacity-50');

  const renderCeilingsSection = () => {
    if (excludeCeilings) return null;

    return (
      <>
        <div className='flex w-full flex-col gap-3'>
          <div className='text-xl font-bold text-gray-900'>Ceilings</div>
          {ceilingsBelowRoof.map((ceiling) => (
            <CeilingBelowRoofComponent
              key={ceiling.uniqueId}
              ceiling={ceiling}
              updateFn={updateCeilingBelowRoof}
              duplicateFn={duplicateCeilingBelowRoof}
              deleteFn={deleteCeiling}
              updateDefaultSizingFn={applyToDefaultSizingItems}
            />
          ))}
          {ceilingsBelowAttic.map((ceiling) => (
            <CeilingBelowAtticComponent
              key={ceiling.uniqueId}
              ceiling={ceiling}
              updateFn={updateCeilingBelowAttic}
              duplicateFn={duplicateCeilingBelowAttic}
              deleteFn={deleteCeiling}
              updateDefaultSizingFn={applyToDefaultSizingItems}
            />
          ))}
        </div>
        <Popover placement='bottom-start'>
          <div className={buttonWrapperClasses(ceilingsAndFloorsDisabled)}>
            <PopoverTrigger
              className='inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-md bg-blue-100 px-3 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
              disabled={ceilingsAndFloorsDisabled}
            >
              <PlusIcon className='h-4 w-4' />
              Ceiling
            </PopoverTrigger>
            {ceilingsAndFloorsDisabled && (
              <Tooltip text='Missing square footage' />
            )}
          </div>
          <PopoverContent className='z-50 flex flex-col rounded-lg bg-white p-2 shadow-lg ring-1 ring-black/5 focus:outline-none'>
            <PopoverClose
              onClick={addCeilingBelowRoof}
              className='flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm font-semibold text-gray-900 hover:bg-gray-100'
            >
              <span>Below Roof Joists</span>
            </PopoverClose>
            <PopoverClose
              onClick={addCeilingOnExposedBeams}
              className='flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm font-semibold text-gray-900 hover:bg-gray-100'
            >
              <span>On Exposed Beams</span>
            </PopoverClose>
            <PopoverClose
              onClick={addCeilingBelowAttic}
              className='flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm font-semibold text-gray-900 hover:bg-gray-100'
            >
              <span>Below Attic</span>
            </PopoverClose>
          </PopoverContent>
        </Popover>
      </>
    );
  };

  const renderWallsSection = () => {
    if (excludeWalls) return null;

    return (
      <>
        <div className='flex w-full flex-col gap-3'>
          <div className='text-xl font-bold text-gray-900'>Exterior Walls</div>
          {aboveGradeWalls.map((wall) => (
            <AboveGradeWallComponent
              key={wall.uniqueId}
              wall={wall}
              updateFn={updateAboveGradeWall}
              duplicateFn={duplicateAboveGradeWall}
              deleteFn={deleteWall}
              updateDefaultSizingFn={applyToDefaultSizingItems}
            />
          ))}
          {belowGradeWalls.map((wall) => (
            <BelowGradeWallComponent
              key={wall.uniqueId}
              wall={wall}
              updateFn={updateBelowGradeWall}
              duplicateFn={duplicateBelowGradeWall}
              deleteFn={deleteWall}
              updateDefaultSizingFn={applyToDefaultSizingItems}
            />
          ))}
        </div>
        <div className='inline-flex items-center gap-3'>
          <div className={buttonWrapperClasses(wallsDisabled)}>
            <CompassPopover
              label='Above Grade Wall'
              onClick={addAboveGradeWall}
              icon={PlusIcon}
              disabled={wallsDisabled}
            />
            {wallsDisabled && (
              <Tooltip
                text={`Missing ${
                  ceilingsAndFloorsDisabled
                    ? 'square footage'
                    : 'ceiling height'
                }`}
              />
            )}
          </div>
          <div className={buttonWrapperClasses(wallsDisabled)}>
            <CompassPopover
              label='Below Grade Wall'
              onClick={addBelowGradeWall}
              icon={PlusIcon}
              disabled={wallsDisabled}
            />
            {wallsDisabled && (
              <Tooltip
                text={`Missing ${
                  ceilingsAndFloorsDisabled
                    ? 'square footage'
                    : 'ceiling height'
                }`}
              />
            )}
          </div>
        </div>
      </>
    );
  };

  const renderFloorsSection = () => {
    if (excludeFloors) return null;

    return (
      <>
        <div className='flex w-full flex-col gap-3'>
          <div className='text-xl font-bold text-gray-900'>Floors</div>
          {floorsOverUnconditionedSpace.map((floor) => (
            <FloorOverUnconditionedSpaceComponent
              key={floor.uniqueId}
              floor={floor}
              updateFn={updateFloorOverUnconditionedSpace}
              duplicateFn={duplicateFloorOverUnconditionedSpace}
              deleteFn={deleteFloor}
              updateDefaultSizingFn={applyToDefaultSizingItems}
            />
          ))}
          {floorsOverExposedSpace.map((floor) => (
            <FloorOverExposedSpaceComponent
              key={floor.uniqueId}
              floor={floor}
              updateFn={updateFloorOverExposedSpace}
              duplicateFn={duplicateFloorOverExposedSpace}
              deleteFn={deleteFloor}
              updateDefaultSizingFn={applyToDefaultSizingItems}
            />
          ))}
          {slabFloors.map((floor) => (
            <SlabFloorComponent
              key={floor.uniqueId}
              floor={floor}
              updateFn={updateSlabFloor}
              duplicateFn={duplicateSlabFloor}
              deleteFn={deleteFloor}
              updateDefaultSizingFn={applyToDefaultSizingItems}
            />
          ))}
          {basementFloors.map((floor) => (
            <BasementFloorComponent
              key={floor.uniqueId}
              floor={floor}
              updateFn={updateBasementFloor}
              duplicateFn={duplicateBasementFloor}
              deleteFn={deleteFloor}
              updateDefaultSizingFn={applyToDefaultSizingItems}
            />
          ))}
        </div>
        <Popover placement='bottom-start'>
          <div className={buttonWrapperClasses(ceilingsAndFloorsDisabled)}>
            <PopoverTrigger
              className='inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-md bg-blue-100 px-3 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
              disabled={ceilingsAndFloorsDisabled}
            >
              <PlusIcon className='h-4 w-4' />
              Floor
            </PopoverTrigger>
            {ceilingsAndFloorsDisabled && (
              <Tooltip text='Missing square footage' />
            )}
          </div>
          <PopoverContent className='z-50 flex flex-col rounded-lg bg-white p-2 shadow-lg ring-1 ring-black/5 focus:outline-none'>
            <PopoverClose
              onClick={addFloorOverUnconditionedSpace}
              className='flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm font-semibold text-gray-900 hover:bg-gray-100'
            >
              <span>Over Unconditioned Space</span>
            </PopoverClose>
            <PopoverClose
              onClick={addFloorOverExposedSpace}
              className='flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm font-semibold text-gray-900 hover:bg-gray-100'
            >
              <span>Over Exposed Space</span>
            </PopoverClose>
            <PopoverClose
              onClick={addSlabFloor}
              className='flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm font-semibold text-gray-900 hover:bg-gray-100'
            >
              <span>Slab Floor</span>
            </PopoverClose>
            <PopoverClose
              onClick={addBasementFloor}
              className='flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm font-semibold text-gray-900 hover:bg-gray-100'
            >
              <span>Basement Floor</span>
            </PopoverClose>
          </PopoverContent>
        </Popover>
      </>
    );
  };

  const renderAppliancesSection = () => (
    <div className='flex w-full flex-col gap-3'>
      <div className='text-xl font-bold text-gray-900'>Appliances</div>
      {appliances.length > 0 && (
        <div className='grid grid-cols-2 gap-3' ref={animate}>
          {appliances.map((appliance) => (
            <div key={appliance.uniqueId}>
              <ApplianceComponent
                appliance={appliance}
                updateFn={updateAppliance}
                duplicateFn={duplicateAppliance}
                deleteFn={deleteAppliance}
                updateDefaultSizingFn={applyToDefaultSizingItems}
              />
            </div>
          ))}
        </div>
      )}
      <Popover placement='bottom-start'>
        <div>
          <PopoverTrigger className='inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-md bg-blue-100 px-3 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'>
            <PlusIcon className='h-4 w-4' />
            Appliance
          </PopoverTrigger>
        </div>
        <PopoverContent className='z-50 flex flex-col rounded-lg bg-white p-2 shadow-lg ring-1 ring-black/5 focus:outline-none'>
          {applianceItems.map((appliance) => (
            <PopoverClose
              key={appliance.itemId}
              onClick={() => addAppliance(appliance.itemId)}
              className='flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm font-semibold text-gray-900 hover:bg-gray-100'
            >
              <span>{appliance.label}</span>
            </PopoverClose>
          ))}
          <PopoverClose
            onClick={() => addAppliance()}
            className='flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm font-semibold text-gray-900 hover:bg-gray-100'
          >
            <span>Custom</span>
          </PopoverClose>
        </PopoverContent>
      </Popover>
    </div>
  );

  const renderSections = () => {
    switch (orderType) {
      case 'windows-doors':
        return (
          <>
            {renderWallsSection()}
            {renderCeilingsSection()}
            {renderFloorsSection()}
            {renderAppliancesSection()}
          </>
        );
      case 'appliances':
        return (
          <>
            {renderAppliancesSection()}
            {renderCeilingsSection()}
            {renderWallsSection()}
            {renderFloorsSection()}
          </>
        );
      default:
        return (
          <>
            {renderCeilingsSection()}
            {renderWallsSection()}
            {renderFloorsSection()}
            {renderAppliancesSection()}
          </>
        );
    }
  };

  return (
    <div className='flex flex-wrap gap-3' ref={animate}>
      {renderSections()}
    </div>
  );
}
